const OPENING_QUOTE = "“";
const CLOSING_QUOTE = "”";

const DIALOGUE_VERB_WORDS = [
  "said",
  "ask",
  "asked",
  "explained",
  "explains",
  "replied",
  "answered",
  "says",
  "whispered",
  "shouted",
  "yelled",
  "cried",
  "muttered",
  "murmured",
  "added",
  "continued",
  "called",
  "responded",
  "insisted",
  "admitted",
  "agreed",
  "noted",
  "told"
];

const DIALOGUE_VERBS = new Set(DIALOGUE_VERB_WORDS);

const DIALOGUE_VERB_OUTPUTS = new Map([["ask", "asked"]]);

const LOWERCASE_TAG_WORDS = new Set([
  "a",
  "an",
  "he",
  "her",
  "his",
  "him",
  "i",
  "it",
  "its",
  "me",
  "my",
  "our",
  "she",
  "them",
  "the",
  "their",
  "they",
  "us",
  "we",
  "you",
  "your"
]);

const DIALOGUE_VERB_PATTERN = DIALOGUE_VERB_WORDS.join("|");

export interface DialogueSegment {
  sequence: number;
  text: string;
}

interface DialogueTagSplit {
  body: string;
  tag: string;
}

interface DialogueInterruption {
  before: string;
  tag: string;
  after: string;
}

export function formatDialogueTranscript(text: string): string {
  const cleaned = stripOuterQuotes(text.replace(/\s+/g, " ").trim());
  if (!cleaned) {
    return "";
  }

  const interruption = splitDialogueInterruption(cleaned);
  if (interruption) {
    return [
      `${OPENING_QUOTE}${formatDialogueBody(interruption.before, true)}${CLOSING_QUOTE}`,
      formatDialogueTag(interruption.tag),
      `${OPENING_QUOTE}${formatDialogueBody(interruption.after, false)}${CLOSING_QUOTE}`
    ].join(" ");
  }

  const split = splitDialogueTag(cleaned);
  if (!split) {
    return `${OPENING_QUOTE}${formatDialogueBody(cleaned, false)}${CLOSING_QUOTE}`;
  }

  return `${OPENING_QUOTE}${formatDialogueBody(split.body, true)}${CLOSING_QUOTE} ${formatDialogueTag(split.tag)}`;
}

export function combineDialogueSegments(segments: DialogueSegment[]): string {
  return [...segments]
    .sort((left, right) => left.sequence - right.sequence)
    .reduce((combined, segment) => {
      const cleaned = segment.text.trim();
      if (!cleaned) {
        return combined;
      }

      if (!combined) {
        return cleaned;
      }

      return joinTranscriptText(combined, cleaned);
    }, "");
}

function splitDialogueInterruption(text: string): DialogueInterruption | null {
  const punctuationMatches = [...text.matchAll(/[,!?]\s+|[.]\s+/g)];

  for (const match of punctuationMatches) {
    const boundaryIndex = match.index;
    if (boundaryIndex === undefined) {
      continue;
    }

    const before = text.slice(0, boundaryIndex + 1).trim();
    const remainder = text.slice(boundaryIndex + match[0].length).trim();
    if (!before || !remainder) {
      continue;
    }

    const tagSplit = splitLeadingDialogueTag(remainder);
    if (!tagSplit || !isLikelyDialogueContinuation(tagSplit.after)) {
      continue;
    }

    return {
      before,
      tag: tagSplit.tag,
      after: tagSplit.after
    };
  }

  return null;
}

function splitLeadingDialogueTag(text: string): { tag: string; after: string } | null {
  const sentenceTag = text.match(/^(.+?[.!?])\s+(.+)$/);
  if (sentenceTag?.[1] && sentenceTag[2]) {
    const tag = sentenceTag[1].trim();
    if (isDialogueTag(tag)) {
      return {
        tag,
        after: sentenceTag[2].trim()
      };
    }
  }

  const commaTag = text.match(/^([^.!?]{1,90}?,)\s+(.+)$/);
  if (commaTag?.[1] && commaTag[2]) {
    const tag = commaTag[1].trim();
    if (isDialogueTag(tag)) {
      return {
        tag,
        after: commaTag[2].trim()
      };
    }
  }

  return null;
}

function isLikelyDialogueContinuation(text: string): boolean {
  const cleaned = stripOuterQuotes(text).trim();
  if (!cleaned) {
    return false;
  }

  const words = cleaned.match(/[A-Za-z']+/g) ?? [];
  return words.length >= 2;
}

function splitDialogueTag(text: string): DialogueTagSplit | null {
  const punctuationMatches = [...text.matchAll(/[,.;:!?]\s+/g)].reverse();

  for (const match of punctuationMatches) {
    const boundaryIndex = match.index;
    if (boundaryIndex === undefined) {
      continue;
    }

    const body = text.slice(0, boundaryIndex + 1).trim();
    const tag = text.slice(boundaryIndex + match[0].length).trim();
    if (body && isDialogueTag(tag)) {
      return { body, tag };
    }
  }

  const verbFirstMatch = text.match(new RegExp(`^(.+?)\\s+((?:${DIALOGUE_VERB_PATTERN})\\b.+)$`, "i"));
  if (verbFirstMatch?.[1] && verbFirstMatch[2] && isDialogueTag(verbFirstMatch[2])) {
    return {
      body: verbFirstMatch[1].trim(),
      tag: verbFirstMatch[2].trim()
    };
  }

  const subjectFirstMatch = text.match(
    new RegExp(
      `^(.+)\\s+([A-Za-z][A-Za-z'-]*(?:\\s+[A-Za-z][A-Za-z'-]*){0,2}\\s+(?:${DIALOGUE_VERB_PATTERN})\\b.*)$`,
      "i"
    )
  );
  if (subjectFirstMatch?.[1] && subjectFirstMatch[2] && isDialogueTag(subjectFirstMatch[2])) {
    return {
      body: subjectFirstMatch[1].trim(),
      tag: subjectFirstMatch[2].trim()
    };
  }

  return null;
}

function isDialogueTag(tag: string): boolean {
  const cleaned = tag.trim();
  if (/[.!?]\s+\S/.test(cleaned)) {
    return false;
  }

  const words = cleaned.toLowerCase().match(/[a-z']+/g);
  if (!words?.length || words.length > 12) {
    return false;
  }

  if (DIALOGUE_VERBS.has(words[0] ?? "")) {
    return true;
  }

  const verbIndex = words.findIndex((word) => DIALOGUE_VERBS.has(word));
  return verbIndex > 0 && verbIndex <= 3;
}

function formatDialogueBody(text: string, hasTag: boolean): string {
  const withoutClosingPunctuation = stripOuterQuotes(text).replace(/[,.;:!?]+$/, "").trim();
  const capitalized = capitalizeFirstLetter(withoutClosingPunctuation);

  if (!capitalized) {
    return "";
  }

  const originalEnding = stripOuterQuotes(text).trim().match(/[!?]$/)?.[0];
  if (hasTag) {
    return `${capitalized}${originalEnding ?? ","}`;
  }

  return `${capitalized}${originalEnding ?? "."}`;
}

function formatDialogueTag(text: string): string {
  const words = text
    .replace(/[.!?]+$/, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return "";
  }

  const verbIndex = words.findIndex((word) =>
    DIALOGUE_VERBS.has(word.toLowerCase().replace(/[^a-z']/g, ""))
  );

  if (verbIndex >= 0 && words[verbIndex]) {
    const normalizedVerb = words[verbIndex].toLowerCase().replace(/[^a-z']/g, "");
    words[verbIndex] = DIALOGUE_VERB_OUTPUTS.get(normalizedVerb) ?? normalizedVerb;
  }

  if (verbIndex === 0 && words[1]) {
    words[1] = capitalizeLikelyName(words[1]);
  } else if (verbIndex > 0 && words[0]) {
    words[0] = capitalizeLikelyName(words[0]);
  }

  return `${words.join(" ")}.`;
}

function stripOuterQuotes(text: string): string {
  return text
    .replace(/^["'“”‘’\s]+/, "")
    .replace(/["'“”‘’\s]+$/, "")
    .trim();
}

function startsWithLowercaseWord(text: string): boolean {
  const firstLetter = text.match(/[A-Za-z]/)?.[0];

  return Boolean(firstLetter && firstLetter === firstLetter.toLowerCase());
}

function joinTranscriptText(previous: string, next: string): string {
  const nextIsTagFragment = startsWithDialogueTagFragment(next);
  const previousText = startsWithLowercaseWord(next) && !nextIsTagFragment
    ? previous.trimEnd().replace(/[.!?]+([)"'\]]*)$/, "$1")
    : previous.trimEnd();
  const separator = /^[,.;:!?]/.test(next) ? "" : " ";

  return `${previousText}${separator}${next}`;
}

function startsWithDialogueTagFragment(text: string): boolean {
  return isDialogueTag(stripOuterQuotes(text).replace(/^[,.;:!?]+\s*/, ""));
}

function capitalizeLikelyName(word: string): string {
  const normalized = word.toLowerCase().replace(/[^a-z']/g, "");
  if (LOWERCASE_TAG_WORDS.has(normalized)) {
    return normalized === "i" ? "I" : normalized;
  }

  return capitalizeFirstLetter(word);
}

function capitalizeFirstLetter(text: string): string {
  return text.replace(/[A-Za-z]/, (letter) => letter.toUpperCase());
}
